import React from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/Card';
import { Users, CheckSquare, DollarSign, TrendingUp, MoreHorizontal } from 'lucide-react';
export function DashboardPreview() {
  return <section className="py-24 bg-[#FAFAFA] overflow-hidden" data-id="element-125">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-126">
        <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-127">
          <div className="lg:w-1/2" data-id="element-128">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-129">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-id="element-130">
                Powerful Admin Dashboard
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-131">
                Get a bird's-eye view of your entire reimbursement program.
                Track active members, monitor daily check-ins, and manage
                payouts from a single, intuitive interface.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-132">
                {['Real-time program utilization metrics', 'Automated fraud detection alerts', 'Customizable reporting widgets', 'Role-based access control'].map((item, i) => <li key={i} className="flex items-center text-slate-700" data-id="element-133">
                    <div className="h-6 w-6 rounded-full bg-teal-100 flex items-center justify-center mr-3 text-[#0A7E8C]" data-id="element-134">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-135">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-136" />
                      </svg>
                    </div>
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <div className="lg:w-1/2 w-full" data-id="element-137">
            <motion.div initial={{
            opacity: 0,
            y: 40,
            rotateX: 10
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotateX: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            type: 'spring'
          }} className="relative perspective-1000" data-id="element-138">
              {/* Main Dashboard Card */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 relative z-10" data-id="element-139">
                {/* Header */}
                <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-4" data-id="element-140">
                  <div data-id="element-141">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-142">
                      Program Overview
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-143">
                      Last updated: Just now
                    </p>
                  </div>
                  <div className="flex gap-2" data-id="element-144">
                    <div className="h-8 w-8 rounded-full bg-slate-100" data-id="element-145" />
                    <div className="h-8 w-8 rounded-full bg-[#FF6B35]" data-id="element-146" />
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-147">
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100" data-id="element-148">
                    <div className="flex items-center justify-between mb-2" data-id="element-149">
                      <span className="text-sm text-slate-500" data-id="element-150">
                        Total Members
                      </span>
                      <Users className="h-4 w-4 text-[#0A7E8C]" data-id="element-151" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-152">
                      12,458
                    </div>
                    <div className="text-xs text-green-600 flex items-center mt-1" data-id="element-153">
                      <TrendingUp className="h-3 w-3 mr-1" data-id="element-154" /> +12% this month
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100" data-id="element-155">
                    <div className="flex items-center justify-between mb-2" data-id="element-156">
                      <span className="text-sm text-slate-500" data-id="element-157">Check-Ins</span>
                      <CheckSquare className="h-4 w-4 text-[#FF6B35]" data-id="element-158" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-159">
                      45,231
                    </div>
                    <div className="text-xs text-green-600 flex items-center mt-1" data-id="element-160">
                      <TrendingUp className="h-3 w-3 mr-1" data-id="element-161" /> +8% this week
                    </div>
                  </div>
                </div>

                {/* Chart Mock */}
                <div className="mb-6" data-id="element-162">
                  <div className="flex justify-between items-center mb-4" data-id="element-163">
                    <h4 className="text-sm font-semibold text-slate-900" data-id="element-164">
                      Monthly Reimbursements
                    </h4>
                    <button className="text-xs text-slate-500 hover:text-[#0A7E8C]" data-id="element-165">
                      View Report
                    </button>
                  </div>
                  <div className="h-32 flex items-end justify-between gap-2" data-id="element-166">
                    {[40, 65, 45, 80, 55, 90, 70].map((h, i) => <div key={i} className="w-full bg-slate-100 rounded-t-sm relative group" data-id="element-167">
                        <motion.div initial={{
                      height: 0
                    }} whileInView={{
                      height: `${h}%`
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      delay: i * 0.1
                    }} className="absolute bottom-0 w-full bg-[#0A7E8C] rounded-t-sm opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-168" />
                      </div>)}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-slate-400" data-id="element-169">
                    <span data-id="element-170">Mon</span>
                    <span data-id="element-171">Tue</span>
                    <span data-id="element-172">Wed</span>
                    <span data-id="element-173">Thu</span>
                    <span data-id="element-174">Fri</span>
                    <span data-id="element-175">Sat</span>
                    <span data-id="element-176">Sun</span>
                  </div>
                </div>

                {/* Recent Activity List */}
                <div data-id="element-177">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-178">
                    Recent Activity
                  </h4>
                  <div className="space-y-3" data-id="element-179">
                    {[{
                    name: 'Sarah J.',
                    action: "Checked in at Gold's Gym",
                    time: '2m ago'
                  }, {
                    name: 'Mike R.',
                    action: 'Completed monthly goal',
                    time: '15m ago'
                  }, {
                    name: 'Emily T.',
                    action: 'New registration verified',
                    time: '1h ago'
                  }].map((item, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-180">
                        <div className="flex items-center gap-3" data-id="element-181">
                          <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center text-[#FF6B35] font-bold text-xs" data-id="element-182">
                            {item.name.charAt(0)}
                          </div>
                          <div data-id="element-183">
                            <p className="font-medium text-slate-900" data-id="element-184">
                              {item.name}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-185">
                              {item.action}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400" data-id="element-186">
                          {item.time}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#FF6B35] rounded-full opacity-10 blur-2xl -z-10" data-id="element-187" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#0A7E8C] rounded-full opacity-10 blur-2xl -z-10" data-id="element-188" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}